import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getAllUsers } from "../../../../redux/slices/userSlice";
import {
  getVoucherDetails,
  updateVoucher,
} from "../../../../redux/slices/voucherSlice";

const UpdateVoucher = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();
  const [voucherForm, setVoucherForm] = useState({
    voucherAccess: "",
    voucherParent: undefined,
    validDate: "",
    voucherType: "oneTime",
    discountPrice: "",
    discountType: "flat",
    description: "",
    condition: "",
    minOrderValue: "",
    maxDiscountAvailable: "",
  });

  const { users } = useSelector((state) => state.userSlice);
  const { voucherDetails } = useSelector((state) => state.voucherSlice);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Fetch users if not available
  useEffect(() => {
    if (!users.length) {
      dispatch(getAllUsers(setIsLoading));
    }
  }, [users, dispatch]);

  // Fetch voucher details
  useEffect(() => {
    if (id) {
      dispatch(getVoucherDetails(id, setIsLoading));
    }
  }, [id, dispatch]);

  // Set form data when voucher details are available
  useEffect(() => {
    if (voucherDetails && Object.keys(voucherDetails).length > 0) {
      const formattedDate = voucherDetails.validDate
        ? new Date(voucherDetails.validDate).toISOString().split("T")[0]
        : "";

      setVoucherForm({
        voucherAccess: voucherDetails.voucherAccess || "",
        voucherParent: voucherDetails.voucherParent || undefined,
        validDate: formattedDate,
        voucherType: voucherDetails.validDate ? "tillDate" : "oneTime",
        discountPrice: voucherDetails.discountPrice || "",
        discountType: voucherDetails.discountType || "flat",
        description: voucherDetails.description || "",
        condition: voucherDetails.condition || "",
        minOrderValue: voucherDetails.minOrderValue || "",
        maxDiscountAvailable: voucherDetails.maxDiscountAvailable || "",
      });
    }
  }, [voucherDetails]);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(updateVoucher(id, voucherForm, setIsLoading,navigate));
  };

  return (
    <div>
      <div className="page-header">
        <h3 className="page-title">Update Voucher</h3>
      </div>
      <div className="row">
        <div className="col-6 mx-auto grid-margin">
          <div className="card">
            <div className="card-body">
              <form className="form-sample">
                <div className="row">
                  <div className="col-12 mb-3">
                    <div className="col-4 mb-3">
                      <div className="form-group">
                        <label htmlFor="voucherAccess">Voucher Access</label>
                        <select
                          className="form-control w-100"
                          value={voucherForm.voucherAccess}
                          onChange={(e) =>
                            setVoucherForm({
                              ...voucherForm,
                              voucherAccess: e.target.value,
                              voucherParent:
                                e.target.value === "public"
                                  ? undefined
                                  : voucherForm.voucherParent,
                            })
                          }
                        >
                          <option value="">Select</option>
                          <option value="public">Public</option>
                          <option value="private">Private</option>
                        </select>
                      </div>
                    </div>

                    {voucherForm.voucherAccess === "private" && (
                      <div className="col-6 mb-3">
                        <div className="form-group">
                          <label htmlFor="voucherParent">User</label>
                          <select
                            className="form-control w-100"
                            value={voucherForm.voucherParent || ""}
                            onChange={(e) =>
                              setVoucherForm({
                                ...voucherForm,
                                voucherParent: e.target.value || undefined,
                              })
                            }
                          >
                            <option value="">Select User Name</option>
                            {users.map((user) => (
                              <option key={user._id} value={user._id}>
                                {user.fullName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="col-6 mb-3">
                    <div className="form-group">
                      <label htmlFor="discountType">Discount Type</label>
                      <select
                        className="form-control w-100 mb-3"
                        value={voucherForm.discountType}
                        onChange={(e) => {
                          setVoucherForm({
                            ...voucherForm,
                            discountType: e.target.value,
                            // Clear discount price when switching types
                            discountPrice: "",
                          });
                        }}
                      >
                        <option value="flat">Flat</option>
                        <option value="percentage">Percentage</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="col-6 mb-3">
                    <div className="form-group">
                      <label htmlFor="discountPrice">
                        {voucherForm.discountType === "flat" 
                          ? "Discount (in $)" 
                          : "Discount (in %)"}
                      </label>
                      <input
                        onChange={(e) => {
                          let value = e.target.value;
                          
                          // For percentage, limit to 1-100
                          if (voucherForm.discountType === "percentage") {
                            const numValue = parseFloat(value);
                            if (numValue > 100) value = "100";
                          }
                          
                          setVoucherForm({
                            ...voucherForm,
                            discountPrice: value,
                          });
                        }}
                        value={voucherForm.discountPrice}
                        placeholder={voucherForm.discountType === "flat" 
                          ? "Enter Amount" 
                          : "Enter Percentage (1-100)"}
                        type="number"
                        min={voucherForm.discountType === "percentage" ? "1" : "0"}
                        max={voucherForm.discountType === "percentage" ? "100" : ""}
                        id="discountPrice"
                        className="form-control w-100"
                        required
                      />
                    </div>
                  </div>

                  <div className="col-6 mb-3">
                    <div className="form-group">
                      <label htmlFor="voucherType">Validity Type</label>
                      <select
                        className="form-control w-100"
                        value={voucherForm.voucherType}
                        onChange={(e) => {
                          setVoucherForm({
                            ...voucherForm,
                            voucherType: e.target.value,
                            validDate:
                              e.target.value === "oneTime"
                                ? ""
                                : voucherForm.validDate,
                          });
                        }}
                      >
                        <option value="oneTime">One Time</option>
                        <option value="tillDate">Till Valid Date</option>
                      </select>
                    </div>
                  </div>

                  {voucherForm.voucherType === "tillDate" && (
                    <div className="col-6 mb-3">
                      <div className="form-group">
                        <label htmlFor="validDate">Valid Till Date</label>
                        <input
                          type="date"
                          id="validDate"
                          className="form-control w-100"
                          value={voucherForm.validDate}
                          onChange={(e) => {
                            setVoucherForm({
                              ...voucherForm,
                              validDate: e.target.value,
                            });
                          }}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="col-6 mb-3">
                    <div className="form-group">
                      <label htmlFor="minOrderValue">Minimum Order Value ($)</label>
                      <input
                        type="number"
                        id="minOrderValue"
                        className="form-control form-control-lg"
                        placeholder="Enter minimum order value"
                        value={voucherForm.minOrderValue}
                        onChange={(e) => {
                          setVoucherForm({
                            ...voucherForm,
                            minOrderValue: e.target.value,
                          });
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="col-6 mb-3">
                    <div className="form-group">
                      <label htmlFor="maxDiscountAvailable">Maximum Discount Available ($)</label>
                      <input
                        type="number"
                        id="maxDiscountAvailable"
                        className="form-control form-control-lg"
                        placeholder="Enter maximum discount amount"
                        value={voucherForm.maxDiscountAvailable}
                        onChange={(e) => {
                          setVoucherForm({
                            ...voucherForm,
                            maxDiscountAvailable: e.target.value,
                          });
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="col-12 mb-3">
                    <div className="form-group">
                      <label htmlFor="description">Voucher Description</label>
                      <textarea
                        id="description"
                        className="form-control"
                        rows="3"
                        placeholder="Enter voucher description"
                        value={voucherForm.description}
                        onChange={(e) => {
                          setVoucherForm({
                            ...voucherForm,
                            description: e.target.value,
                          });
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="col-12 mb-3">
                    <div className="form-group">
                      <label htmlFor="condition">Terms & Conditions</label>
                      <textarea
                        id="condition"
                        className="form-control"
                        rows="3"
                        placeholder="Enter terms and conditions"
                        value={voucherForm.condition}
                        onChange={(e) => {
                          setVoucherForm({
                            ...voucherForm,
                            condition: e.target.value,
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="d-flex gap-3 mt-4">
                  <button
                    className="btn_secondary"
                    type="button"
                    onClick={() => navigate(-1)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn_primary"
                    onClick={handleSubmit}
                  >
                    {isLoading ? "Updating..." : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateVoucher;
